import { useState, useEffect, useRef } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';

/* ─── Shared slider ─── */
function NativeSlider({ value, min, max, step, onChange, disabled }) {
  const pct = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  return (
    <div className="relative flex items-center h-10" style={{ opacity: disabled ? 0.35 : 1 }}>
      <div className="absolute left-0 right-0 h-[5px] rounded-full"
        style={{ background: 'var(--bg-tertiary, #e5e7eb)' }} />
      <div className="absolute left-0 h-[5px] rounded-full"
        style={{ width: `${pct}%`, background: 'var(--accent-primary, #3b82f6)', transition: 'width .05s' }} />
      <input type="range" min={min} max={max} step={step} value={value} onChange={onChange}
        disabled={disabled}
        className="sym-thumb w-full h-10 absolute left-0 cursor-pointer bg-transparent appearance-none disabled:cursor-default" />
    </div>
  );
}

/* ─── Editable value badge ─── */
function EditableBadge({ displayValue, rawValue, min, max, step, onCommit, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const startEdit = () => {
    if (disabled) return;
    setDraft(rawValue !== null && rawValue !== undefined ? String(rawValue) : '');
    setEditing(true);
    setTimeout(() => { inputRef.current?.select(); }, 0);
  };

  const commit = () => {
    const parsed = step % 1 === 0 ? parseInt(draft) : parseFloat(draft);
    if (!isNaN(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      onCommit(clamped);
    }
    setEditing(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={min} max={max} step={step}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        className="text-sm font-mono font-semibold px-2 py-0.5 rounded-full text-center w-24 outline-none"
        style={{
          background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 12%, transparent)',
          color: 'var(--accent-primary, #3b82f6)',
          border: '1.5px solid var(--accent-primary, #3b82f6)',
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      title="Click to type a value"
      className="text-sm font-mono font-semibold px-2 py-0.5 rounded-full transition-all active:scale-95"
      style={{
        background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 12%, transparent)',
        color: 'var(--accent-primary, #3b82f6)',
        border: '1.5px solid transparent',
        cursor: disabled ? 'default' : 'text',
      }}>
      {displayValue}
    </button>
  );
}

function SliderRow({ label, hint, value, displayValue, min, max, step, onChange, onDirectInput }) {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-1">
        <label className="text-[15px] font-medium" style={{ color: 'var(--text-primary, #111)' }}>{label}</label>
        <EditableBadge
          displayValue={displayValue ?? value}
          rawValue={value}
          min={min} max={max} step={step}
          onCommit={onDirectInput}
        />
      </div>
      <NativeSlider value={value} min={min} max={max} step={step} onChange={onChange} />
      {hint && <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{hint}</p>}
    </div>
  );
}

function OverrideSliderRow({ label, hint, value, displayValue, min, max, step, onChange, onDirectInput, enabled, onToggle, defaultBadge }) {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
          <label className="text-[15px] font-medium truncate"
            style={{ color: enabled ? 'var(--text-primary, #111)' : 'var(--text-secondary, #9ca3af)' }}>
            {label}
          </label>
          {!enabled && (
            <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
              style={{ background: 'var(--bg-tertiary, #e5e7eb)', color: 'var(--text-secondary, #6b7280)' }}>
              {defaultBadge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {enabled && (
            <EditableBadge
              displayValue={displayValue ?? value}
              rawValue={value}
              min={min} max={max} step={step}
              onCommit={onDirectInput}
              disabled={!enabled}
            />
          )}
          <button type="button" onClick={onToggle}
            className="relative w-10 h-5 rounded-full transition-colors flex-shrink-0"
            style={{ background: enabled ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-tertiary, #d1d5db)' }}>
            <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
              style={{ left: enabled ? '22px' : '2px' }} />
          </button>
        </div>
      </div>
      <NativeSlider value={value ?? min} min={min} max={max} step={step} onChange={onChange} disabled={!enabled} />
      {hint && <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{hint}</p>}
    </div>
  );
}

function SwapModeRow({ value, onChange, enabled, onToggle, label, hint, defaultBadge }) {
  const modes = ['POINTS', 'PERCENTAGE', 'CURRENCY'];
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <label className="text-[15px] font-medium"
            style={{ color: enabled ? 'var(--text-primary, #111)' : 'var(--text-secondary, #9ca3af)' }}>
            {label}
          </label>
          {!enabled && (
            <span className="text-xs px-1.5 py-0.5 rounded"
              style={{ background: 'var(--bg-tertiary, #e5e7eb)', color: 'var(--text-secondary, #6b7280)' }}>
              {defaultBadge}
            </span>
          )}
        </div>
        <button type="button" onClick={onToggle}
          className="relative w-10 h-5 rounded-full transition-colors"
          style={{ background: enabled ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-tertiary, #d1d5db)' }}>
          <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
            style={{ left: enabled ? '22px' : '2px' }} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1.5"
        style={{ opacity: enabled ? 1 : 0.35, pointerEvents: enabled ? 'auto' : 'none' }}>
        {modes.map(m => (
          <button key={m} type="button" onClick={() => onChange(m)}
            className="rounded-xl py-2 text-xs font-semibold transition-all active:scale-95"
            style={{
              background: value === m ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-primary, #fff)',
              color: value === m ? '#fff' : 'var(--text-secondary, #6b7280)',
              border: `1px solid ${value === m ? 'var(--accent-primary, #3b82f6)' : 'var(--border-color, #e5e7eb)'}`,
            }}>
            {m}
          </button>
        ))}
      </div>
      <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{hint}</p>
    </div>
  );
}

function DayPicker({ value, onChange, label, hint, days }) {
  return (
    <div className="py-3">
      <p className="text-[15px] font-medium mb-2" style={{ color: 'var(--text-primary, #111)' }}>{label}</p>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          const n = i + 1; const active = value === n;
          return (
            <button key={d} type="button" onClick={() => onChange(n)}
              className="rounded-lg py-2 text-xs font-semibold transition-all active:scale-95"
              style={{
                background: active ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-secondary, #f3f4f6)',
                color: active ? '#fff' : 'var(--text-secondary, #6b7280)',
              }}>
              {d}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{hint}</p>
    </div>
  );
}

function SectionContent({ activeSection, formData, set, tog, t }) {
  const minAboveMax = formData.min_lot_size > formData.max_lot_size;
  const db = t('symbolSettings.defaultBadge');
  const days = [
    t('symbolSettings.dayMon'), t('symbolSettings.dayTue'), t('symbolSettings.dayWed'),
    t('symbolSettings.dayThu'), t('symbolSettings.dayFri'), t('symbolSettings.daySat'),
    t('symbolSettings.daySun'),
  ];

  if (activeSection === 'pricing') return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('symbolSettings.sectionPricing')}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{t('symbolSettings.pricingDesc')}</p>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <SliderRow label={t('symbolSettings.spreadMarkup')} hint={t('symbolSettings.spreadMarkupHint')}
          value={formData.spread_markup} displayValue={`${formData.spread_markup} ${t('symbolSettings.pips')}`}
          min={0} max={1000000} step={0.1}
          onChange={e => set('spread_markup', parseFloat(e.target.value))}
          onDirectInput={v => set('spread_markup', v)} />
        <SliderRow label={t('symbolSettings.commissionPerLot')} hint={t('symbolSettings.commissionPerLotHint')}
          value={formData.commission_per_lot} displayValue={`$${formData.commission_per_lot}`}
          min={0} max={50} step={0.5}
          onChange={e => set('commission_per_lot', parseFloat(e.target.value))}
          onDirectInput={v => set('commission_per_lot', v)} />
        <SliderRow label={t('symbolSettings.swapMarkup')} hint={t('symbolSettings.swapMarkupHint')}
          value={formData.swap_markup} displayValue={`${formData.swap_markup} ${t('symbolSettings.pts')}`}
          min={0} max={5} step={0.1}
          onChange={e => set('swap_markup', parseFloat(e.target.value))}
          onDirectInput={v => set('swap_markup', v)} />
        <DayPicker
          value={formData.triple_swap_day}
          onChange={v => set('triple_swap_day', v)}
          label={t('symbolSettings.tripleSwapDay')}
          hint={t('symbolSettings.tripleSwapDayHint')}
          days={days}
        />
      </div>
    </div>
  );

  if (activeSection === 'risk') return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('symbolSettings.sectionRisk')}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{t('symbolSettings.riskDesc')}</p>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <SliderRow label={t('symbolSettings.maxLeverage')} hint={t('symbolSettings.maxLeverageHint')}
          value={formData.max_leverage} displayValue={`1:${formData.max_leverage}`}
          min={1} max={2000} step={1}
          onChange={e => set('max_leverage', parseInt(e.target.value))}
          onDirectInput={v => set('max_leverage', v)} />
        <SliderRow label={t('symbolSettings.maxLotSize')} hint={t('symbolSettings.maxLotSizeHint')}
          value={formData.max_lot_size} displayValue={`${formData.max_lot_size} ${t('symbolSettings.lots')}`}
          min={0.01} max={500} step={0.01}
          onChange={e => set('max_lot_size', parseFloat(e.target.value))}
          onDirectInput={v => set('max_lot_size', v)} />
        <SliderRow label={t('symbolSettings.minLotSize')} hint={t('symbolSettings.minLotSizeHint')}
          value={formData.min_lot_size} displayValue={`${formData.min_lot_size} ${t('symbolSettings.lots')}`}
          min={0.01} max={10} step={0.01}
          onChange={e => set('min_lot_size', parseFloat(e.target.value))}
          onDirectInput={v => set('min_lot_size', v)} />
        <SliderRow label={t('symbolSettings.lotStep')} hint={t('symbolSettings.lotStepHint')}
          value={formData.lot_step} displayValue={`${formData.lot_step}`}
          min={0.001} max={1} step={0.001}
          onChange={e => set('lot_step', parseFloat(e.target.value))}
          onDirectInput={v => set('lot_step', v)} />
        {minAboveMax && (
          <div className="py-2">
            <p className="text-xs" style={{ color: 'var(--accent-danger, #ef4444)' }}>
              {t('symbolSettings.minAboveMaxError')}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  if (activeSection === 'execution') return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('symbolSettings.sectionExecution')}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{t('symbolSettings.executionDesc')}</p>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <SliderRow label={t('symbolSettings.stopLevel')} hint={t('symbolSettings.stopLevelHint')}
          value={formData.stop_level} displayValue={`${formData.stop_level} ${t('symbolSettings.pips')}`}
          min={0} max={100} step={1}
          onChange={e => set('stop_level', parseInt(e.target.value))}
          onDirectInput={v => set('stop_level', v)} />
        <SliderRow label={t('symbolSettings.freezeLevel')} hint={t('symbolSettings.freezeLevelHint')}
          value={formData.freeze_level} displayValue={`${formData.freeze_level} ${t('symbolSettings.pips')}`}
          min={0} max={50} step={1}
          onChange={e => set('freeze_level', parseInt(e.target.value))}
          onDirectInput={v => set('freeze_level', v)} />
      </div>
    </div>
  );

  if (activeSection === 'overrides') return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('symbolSettings.sectionOverrides')}</h3>
      <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>{t('symbolSettings.overridesDesc')}</p>
      <div className="rounded-xl px-3 py-2.5 mb-4 flex gap-2"
        style={{
          background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--accent-primary,#3b82f6) 20%, transparent)',
        }}>
        <span className="text-base leading-none mt-0.5">ℹ️</span>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary, #6b7280)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>{t('symbolSettings.overridesInfoTitle')}</strong>{' '}
          {t('symbolSettings.overridesInfoDesc')}
        </p>
      </div>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <OverrideSliderRow label={t('symbolSettings.contractSize')} hint={t('symbolSettings.contractSizeHint')}
          defaultBadge={db}
          value={formData.contract_size ?? 100000} displayValue={`${formData.contract_size ?? 100000}`}
          min={1} max={1000000} step={1}
          enabled={formData._en_contract_size} onToggle={() => tog('contract_size', 100000)}
          onChange={e => set('contract_size', parseInt(e.target.value))}
          onDirectInput={v => set('contract_size', v)} />
        <OverrideSliderRow label={t('symbolSettings.priceScale')} hint={t('symbolSettings.priceScaleHint')}
          defaultBadge={db}
          value={formData.price_scale ?? 5} displayValue={`${formData.price_scale ?? 5} ${t('symbolSettings.decimals')}`}
          min={0} max={8} step={1}
          enabled={formData._en_price_scale} onToggle={() => tog('price_scale', 5)}
          onChange={e => set('price_scale', parseInt(e.target.value))}
          onDirectInput={v => set('price_scale', v)} />
        <OverrideSliderRow label={t('symbolSettings.swapLong')} hint={t('symbolSettings.swapLongHint')}
          defaultBadge={db}
          value={formData.swap_long ?? -1} displayValue={`${formData.swap_long ?? -1}`}
          min={-20} max={20} step={0.001}
          enabled={formData._en_swap_long} onToggle={() => tog('swap_long', -1)}
          onChange={e => set('swap_long', parseFloat(e.target.value))}
          onDirectInput={v => set('swap_long', v)} />
        <OverrideSliderRow label={t('symbolSettings.swapShort')} hint={t('symbolSettings.swapShortHint')}
          defaultBadge={db}
          value={formData.swap_short ?? -1} displayValue={`${formData.swap_short ?? -1}`}
          min={-20} max={20} step={0.001}
          enabled={formData._en_swap_short} onToggle={() => tog('swap_short', -1)}
          onChange={e => set('swap_short', parseFloat(e.target.value))}
          onDirectInput={v => set('swap_short', v)} />
        <SwapModeRow
          label={t('symbolSettings.swapMode')} hint={t('symbolSettings.swapModeHint')} defaultBadge={db}
          value={formData.swap_mode ?? 'POINTS'} onChange={v => set('swap_mode', v)}
          enabled={formData._en_swap_mode} onToggle={() => tog('swap_mode', 'POINTS')} />
      </div>
    </div>
  );

  return null;
}

const isSet = v => v !== null && v !== undefined;

export default function SymbolSettingsModal({ symbol, onClose, onSave, fetchSymbolSettings }) {
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('pricing');

  const SECTIONS = [
    { id: 'pricing',   label: t('symbolSettings.sectionPricing'),   icon: '💰' },
    { id: 'risk',      label: t('symbolSettings.sectionRisk'),       icon: '📊' },
    { id: 'execution', label: t('symbolSettings.sectionExecution'),  icon: '⚙️' },
    { id: 'overrides', label: t('symbolSettings.sectionOverrides'),  icon: '🔧' },
  ];

  const [formData, setFormData] = useState({
    spread_markup: 0.0, commission_per_lot: 0.0, swap_markup: 0.0, triple_swap_day: 3,
    max_leverage: 500, max_lot_size: 100.0, min_lot_size: 0.01, lot_step: 0.01,
    stop_level: 10, freeze_level: 5,
    contract_size: null, price_scale: null, swap_long: null, swap_short: null, swap_mode: null,
    _en_contract_size: false, _en_price_scale: false, _en_swap_long: false,
    _en_swap_short: false, _en_swap_mode: false,
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const s = (await fetchSymbolSettings(symbol)).data;
      setFormData({
        spread_markup:      s.spread_markup      ?? 0.0,
        commission_per_lot: s.commission_per_lot ?? 0.0,
        swap_markup:        s.swap_markup        ?? 0.0,
        triple_swap_day:    s.triple_swap_day    ?? 3,
        max_leverage:       s.max_leverage       ?? 500,
        max_lot_size:       s.max_lot_size       ?? 100.0,
        min_lot_size:       s.min_lot_size       ?? 0.01,
        lot_step:           s.lot_step           ?? 0.01,
        stop_level:         s.stop_level         ?? 10,
        freeze_level:       s.freeze_level       ?? 5,
        contract_size: s.contract_size ?? null,
        price_scale:   s.price_scale   ?? null,
        swap_long:     s.swap_long     ?? null,
        swap_short:    s.swap_short    ?? null,
        swap_mode:     s.swap_mode     ?? null,
        _en_contract_size: isSet(s.contract_size),
        _en_price_scale:   isSet(s.price_scale),
        _en_swap_long:     isSet(s.swap_long),
        _en_swap_short:    isSet(s.swap_short),
        _en_swap_mode:     isSet(s.swap_mode),
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const set = (f, v) => setFormData(p => ({ ...p, [f]: v }));
  const tog = (field, defaultVal) => {
    setFormData(p => {
      const enabling = !p[`_en_${field}`];
      return { ...p, [`_en_${field}`]: enabling, [field]: enabling ? (p[field] ?? defaultVal) : null };
    });
  };

  const activeOverrides = ['contract_size','price_scale','swap_long','swap_short','swap_mode']
    .filter(k => formData[`_en_${k}`]).length;
  const minAboveMax = formData.min_lot_size > formData.max_lot_size;

  const handleSubmit = () => {
    onSave({
      spread_markup:      formData.spread_markup,
      commission_per_lot: formData.commission_per_lot,
      swap_markup:        formData.swap_markup,
      triple_swap_day:    formData.triple_swap_day,
      max_leverage:       formData.max_leverage,
      max_lot_size:       formData.max_lot_size,
      min_lot_size:       formData.min_lot_size,
      lot_step:           formData.lot_step,
      stop_level:         formData.stop_level,
      freeze_level:       formData.freeze_level,
      contract_size: formData._en_contract_size ? parseInt(formData.contract_size)   : null,
      price_scale:   formData._en_price_scale   ? parseInt(formData.price_scale)     : null,
      swap_long:     formData._en_swap_long     ? parseFloat(formData.swap_long)     : null,
      swap_short:    formData._en_swap_short    ? parseFloat(formData.swap_short)    : null,
      swap_mode:     formData._en_swap_mode     ? formData.swap_mode                 : null,
    });
  };

  return (
    <>
      <style>{`
        .sym-thumb::-webkit-slider-thumb {
          -webkit-appearance: none; width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          box-shadow: 0 1px 4px rgba(0,0,0,.25), 0 0 0 3px color-mix(in srgb, var(--accent-primary,#3b82f6) 18%, transparent);
          border: 2.5px solid #fff; cursor: pointer; transition: transform .1s;
        }
        .sym-thumb:active::-webkit-slider-thumb { transform: scale(1.15); }
        .sym-thumb:disabled::-webkit-slider-thumb { cursor: default; }
        .sym-thumb::-moz-range-thumb { width: 26px; height: 26px; border-radius: 50%; background: var(--accent-primary, #3b82f6); border: none; cursor: pointer; }
        /* hide number input spinners */
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        @media (max-width: 767px) {
          .sym-backdrop { align-items: flex-end; }
          .sym-dialog { width: 100%; max-width: 100%; border-radius: 20px 20px 0 0; max-height: 92dvh; flex-direction: column; animation: symSlideUp .28s cubic-bezier(.32,.72,0,1); }
          .sym-handle { display: flex; } .sym-sidebar { display: none; } .sym-tabbar { display: flex; }
          .sym-footer { padding: 12px 16px; padding-bottom: max(16px, env(safe-area-inset-bottom)); }
          .sym-footer button { padding-top: 14px; padding-bottom: 14px; border-radius: 16px; font-size: 16px; font-weight: 600; }
        }
        @media (min-width: 768px) {
          .sym-backdrop { align-items: center; justify-content: center; padding: 16px; }
          .sym-dialog { width: 100%; max-width: 896px; border-radius: 16px; max-height: 90vh; flex-direction: row; border: 1px solid var(--border-color); }
          .sym-handle { display: none; } .sym-sidebar { display: flex; } .sym-tabbar { display: none; }
          .sym-footer { padding: 24px; }
          .sym-footer button { padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 500; }
        }
        @keyframes symSlideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      <div className="sym-backdrop fixed inset-0 z-50 flex"
        style={{ background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
        onClick={e => e.target === e.currentTarget && onClose()}>

        <div className="sym-dialog flex overflow-hidden" style={{ background: 'var(--bg-primary, #fff)' }}>

          {/* Desktop sidebar */}
          <div className="sym-sidebar w-56 flex-col flex-shrink-0 overflow-y-auto p-4"
            style={{ background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)' }}>
            <h3 className="text-sm font-semibold mb-3 px-2" style={{ color: 'var(--text-secondary)' }}>
              {t('symbolSettings.sectionsLabel')}
            </h3>
            <div className="space-y-1">
              {SECTIONS.map(s => (
                <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between"
                  style={{
                    background: activeSection === s.id ? 'var(--accent-primary)' : 'transparent',
                    color: activeSection === s.id ? '#fff' : 'var(--text-secondary)',
                  }}>
                  <span><span className="mr-2">{s.icon}</span>{s.label}</span>
                  {s.id === 'overrides' && activeOverrides > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                      style={{
                        background: activeSection === s.id ? 'rgba(255,255,255,0.25)' : 'color-mix(in srgb, #f59e0b 20%, transparent)',
                        color: activeSection === s.id ? '#fff' : '#92400e',
                      }}>
                      {activeOverrides}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Main column */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">

            {/* Mobile drag handle */}
            <div className="sym-handle justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-[5px] rounded-full" style={{ background: 'var(--border-color, #d1d5db)' }} />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
              style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold">
                    {t('symbolSettings.settingsTitle').replace('{symbol}', symbol)}
                  </h2>
                  {activeOverrides > 0 && !loading && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: 'color-mix(in srgb, #f59e0b 15%, transparent)', color: '#92400e' }}>
                      {activeOverrides !== 1
                        ? t('symbolSettings.overridesActive').replace('{count}', activeOverrides)
                        : t('symbolSettings.overrideActive').replace('{count}', activeOverrides)}
                    </span>
                  )}
                </div>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {t('symbolSettings.headerSubtitle')}
                </p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile tab bar */}
            <div className="sym-tabbar gap-1 px-3 py-2 flex-shrink-0 overflow-x-auto"
              style={{ borderBottom: '1px solid var(--border-color)' }}>
              {SECTIONS.map(({ id, label, icon }) => (
                <button key={id} type="button" onClick={() => setActiveSection(id)}
                  className="flex-shrink-0 flex items-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold transition-all active:scale-95"
                  style={{
                    background: activeSection === id ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-secondary, #f3f4f6)',
                    color: activeSection === id ? '#fff' : 'var(--text-secondary, #6b7280)',
                  }}>
                  <span>{icon}</span>
                  {label}
                  {id === 'overrides' && activeOverrides > 0 && (
                    <span className="text-xs px-1.5 rounded-full"
                      style={{
                        background: activeSection === id ? 'rgba(255,255,255,0.3)' : 'color-mix(in srgb,#f59e0b 25%,transparent)',
                        color: activeSection === id ? '#fff' : '#92400e',
                      }}>
                      {activeOverrides}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Body */}
            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary, #3b82f6)' }} />
                <p className="text-sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                  {t('symbolSettings.loadingSettings')}
                </p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto px-6 py-5" style={{ WebkitOverflowScrolling: 'touch' }}>
                <SectionContent activeSection={activeSection} formData={formData} set={set} tog={tog} t={t} />
                <div className="h-2" />
              </div>
            )}

            {/* Footer */}
            <div className="sym-footer flex gap-3 flex-shrink-0"
              style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
              <button type="button" onClick={onClose} disabled={loading}
                className="flex-1 bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50">
                {t('common.cancel')}
              </button>
              <button type="button" onClick={handleSubmit} disabled={loading || minAboveMax}
                className="flex-1 bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                {t('symbolSettings.saveSettings')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}